import { useState } from 'react';
import { useFetch, useRequest } from '../../../hooks';
import { notify } from '../../../app';
import {
  getOutboundNumbers,
  getShortAgents,
  makeAgentCall,
} from '../../../api';

export type HookWebLinkGeneratorProps = {
  agentId: string;
};

export type CallManagementFormProps = {
  agentId: string;
  phone: string;
  pinCode?: string;
  outboundPhoneId: string;
  backgroundInfo?: string;
};

export const useCallManagement = () => {
  const { loading: isAgentsLoading, data: agentsOptions } =
    useFetch(async () => {
      const data = await getShortAgents();

      return (data ?? []).map(d => ({
        value: d.id ?? '',
        label: d.title,
      }));
    }, []);

  const [formId, setFormId] = useState(1);
  const [form, setForm] = useState<CallManagementFormProps | null>(null);

  const [handleCall, isCalling] = useRequest(
    _signal => {
      const { phone: phoneValue, pinCode: pinCodeValue } =
        form || ({} as CallManagementFormProps);
      const phone = phoneValue?.replace(/[\s\-()]/g, '');
      const pinCode = pinCodeValue ? pinCodeValue.replace(/[^\d#]/g, '') : null;

      return makeAgentCall({
        outboundPhoneId: form?.outboundPhoneId || '',
        destinationPhone: phone,
        agentId: form?.agentId || '',
        pinCode: pinCode || undefined,
        backgroundInfo: form?.backgroundInfo || undefined,
      });
    },
    {
      onSuccess: () => {
        notify.success('A call has been initiated');
      },
      onError: () => {
        notify.error('Failed to initiate a call');
      },
      deps: [
        form?.phone,
        form?.agentId,
        form?.pinCode,
        form?.backgroundInfo,
        form?.outboundPhoneId,
      ],
    }
  );

  const { intializing: loadingOutbound, data: outboundPhones } =
    useFetch(async () => {
      const res = await getOutboundNumbers();
      return (res ?? []).map(d => ({
        value: d.id,
        label: d.phoneNumber,
      })) as OptionType[];
    }, []);

  return {
    form,
    formId: String(formId),
    onChange: setForm,
    setFormId,
    setForm,
    agentsOptions,
    handleCall,
    isCalling,
    outboundPhones,
    loadingOutbound,
    isAgentsLoading,
  };
};
